// src/nexus/capitalPulse.js
//
// Capital-world extractor for CapitalView's "Portfolio Twin" contract card.
// Mirrors the validate-or-null discipline established in systemPulse.js/
// operationsPulse.js/learningPulse.js/proofPulse.js.
//
// Root cause this closes: CapitalView had zero live props and rendered
// static "8"/"3"/"6" literals for Active/Candidates/Journal, captioned
// "Illustrativer Paper-Snapshot" — genuinely disclosed as illustrative, but
// stale and disconnected from the real, already-fetched "capital" world
// (WORLD_ENDPOINTS.capital already includes "portfolio-twin", whose real
// openCount this file now surfaces).
//
// Verified against the real backend response shape by reading
// services/portfolioTwin.service.js#getPortfolioTwinSnapshot directly
// (davidhuecker-bit/hqs-backend):
//   GET /api/admin/portfolio-twin ->
//     { success, portfolioTwin: { openCount, closedCount, openPositions,
//       closedPositions, totalAllocatedEur, unrealizedPnlEur, ... } }
//
// "Candidates" (a Governor-gated queue of scored-but-not-yet-admitted
// symbols) and "Journal" (a decision log) have no verified equivalent
// anywhere in the "capital" world's endpoint set (saved-capital, near-
// misses, guardian-status-summary, portfolio-twin, virtual-positions,
// interface-state) — surfaced as null/not-delivered rather than guessed.

const USABLE_ENDPOINT_STATES = new Set(["live", "partial"]);

function findEndpoint(endpointResults, id) {
  if (!Array.isArray(endpointResults)) return null;
  return endpointResults.find((entry) => entry && entry.id === id) || null;
}

function isUsable(endpoint) {
  return !!endpoint && USABLE_ENDPOINT_STATES.has(endpoint.state);
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isNonNegativeInteger(value) {
  return typeof value === "number" && Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

function endpointBody(endpoint) {
  if (!endpoint) return null;
  const raw = endpoint.data;
  return isPlainObject(raw) ? raw : null;
}

function pushMissing(missingFields, field) {
  if (!missingFields.includes(field)) missingFields.push(field);
}

export function extractPortfolioTwinCounts(endpointResults, missingFields = []) {
  const endpoint = findEndpoint(endpointResults, "portfolio-twin");
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "portfolioTwinCounts");
    return { openCount: null, closedCount: null };
  }
  const body = endpointBody(endpoint);
  const twin = isPlainObject(body?.portfolioTwin) ? body.portfolioTwin : body;
  const openCount = isNonNegativeInteger(twin?.openCount) ? twin.openCount : null;
  const closedCount = isNonNegativeInteger(twin?.closedCount) ? twin.closedCount : null;
  if (openCount === null && closedCount === null) pushMissing(missingFields, "portfolioTwinCounts");
  return { openCount, closedCount };
}

export function normalizeCapitalPulse({ endpointResults } = {}) {
  const missingFields = [];
  const { openCount, closedCount } = extractPortfolioTwinCounts(endpointResults, missingFields);
  return { openCount, closedCount, missingFields };
}
